import { toStyleNameKey } from './styleNameKey';

/**
 * Resolves an extracted style label to a beerStyles catalog document id.
 */
export function resolveCatalogStyleId(styleLabel, styleMapping = {}) {
  const trimmed = typeof styleLabel === 'string' ? styleLabel.trim() : '';
  if (!trimmed) {
    return null;
  }
  return styleMapping[trimmed] || styleMapping[toStyleNameKey(trimmed)] || null;
}

/**
 * Unique extracted style names that are not yet in the catalog and have no type.
 */
export function unknownScrapedStyles(beers, styleMapping = {}, styleTypeByName = {}) {
  const seen = new Set();
  const unknown = [];
  (Array.isArray(beers) ? beers : []).forEach((beer) => {
    const styleName = typeof beer?.beer_style === 'string' ? beer.beer_style.trim() : '';
    if (!styleName || seen.has(styleName)) {
      return;
    }
    seen.add(styleName);
    if (resolveCatalogStyleId(styleName, styleMapping) || styleTypeByName[styleName]) {
      return;
    }
    unknown.push(styleName);
  });
  return unknown;
}

/**
 * A scraped beer can be imported when it has a name and either a catalog
 * style match or an assigned beerStyleTypes name.
 */
export function isScrapedBeerReadyToImport(beer, styleMapping = {}, styleTypeByName = {}) {
  const beerName = typeof beer?.beer_name === 'string' ? beer.beer_name.trim() : '';
  const styleName = typeof beer?.beer_style === 'string' ? beer.beer_style.trim() : '';
  if (!beerName || !styleName) {
    return false;
  }
  if (resolveCatalogStyleId(styleName, styleMapping)) {
    return true;
  }
  return Boolean(styleTypeByName[styleName]);
}

/**
 * Import is enabled when every selected row is ready.
 */
export function canImportScrapedBeers(
  beers,
  selectedIndices,
  styleMapping = {},
  styleTypeByName = {},
) {
  if (!Array.isArray(beers) || beers.length === 0) {
    return false;
  }
  const indices = Array.isArray(selectedIndices) ? selectedIndices : [];
  if (indices.length === 0) {
    return false;
  }
  return indices.every((index) => {
    if (index < 0 || index >= beers.length) {
      return false;
    }
    return isScrapedBeerReadyToImport(beers[index], styleMapping, styleTypeByName);
  });
}

/**
 * Builds an addBeer payload from a scraped beer.
 * Catalog hit → { style: id }. Unknown name + type → { styleName, styleType }.
 */
export function transformBeerToImport(beer, styleMapping, styleTypeByName, parentBreweryId) {
  const beerName = typeof beer?.beer_name === 'string' ? beer.beer_name.trim() : '';
  const importedStyle = typeof beer?.beer_style === 'string' ? beer.beer_style.trim() : '';
  if (!beerName || !importedStyle) {
    return null;
  }

  const catalogId = resolveCatalogStyleId(importedStyle, styleMapping);
  const assignedType = styleTypeByName?.[importedStyle];
  if (!catalogId && !assignedType) {
    return null;
  }

  const beerToImport = {
    name: beerName,
    breweryId: parentBreweryId,
    abv: parseScrapedAbv(beer.beer_abv),
  };

  if (catalogId) {
    beerToImport.style = catalogId;
  } else {
    beerToImport.styleName = importedStyle;
    beerToImport.styleType = assignedType;
  }

  const beerDescription = typeof beer.beer_description === 'string'
    ? beer.beer_description.trim()
    : '';
  if (beerDescription) {
    beerToImport.description = beerDescription;
  }

  return beerToImport;
}

export function parseScrapedAbv(abvString) {
  if (!abvString) {
    return null;
  }
  const abvStr = abvString.toString().trim();
  const abvNum = parseFloat(abvStr.replace('%', ''));
  return Number.isNaN(abvNum) ? null : abvNum;
}

export function extractBeersFromScrapingResult(scrapingResult) {
  const jsonData = scrapingResult?.json || scrapingResult?.data?.json || {};
  return Array.isArray(jsonData.beers) ? jsonData.beers : [];
}
